import { AlertCircle, Loader2, RotateCcw, Sparkles, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { COMPANY_STUDIO_PATHS } from "@/routes/company/routes.studio";
import {
  rebuildStudioJob,
  type StudioJob,
  takeStudioJob,
  useStudioJobs,
} from "./studio-jobs";

const pathFor = (job: StudioJob) =>
  job.module === "workflows"
    ? COMPANY_STUDIO_PATHS.WORKFLOW_DESCRIBE(job.target)
    : job.module === "email"
      ? COMPANY_STUDIO_PATHS.EMAIL_TEMPLATES_BUILDER(job.target)
      : COMPANY_STUDIO_PATHS.DOCUMENTS_BUILDER(job.target);

// Floating list of Studio generations still running (or finished but not yet
// applied) after the user left the page that started them, and failed ones —
// kept until rebuilt or dismissed. A job's own page shows it in full, so its
// row is hidden there.
export const StudioJobsDock = () => {
  const { t } = useTranslation(["studio"]);
  const { pathname } = useLocation();
  const jobs = useStudioJobs().filter((j) => pathFor(j) !== pathname);
  if (!jobs.length) return null;

  const title = (job: StudioJob) => {
    if (job.phase === "error") return t("studio:jobs.generation_failed");
    if (job.phase === "done") return t("studio:jobs.content_ready");
    return job.module === "workflows"
      ? t("studio:jobs.building_workflow")
      : job.module === "email"
        ? t("studio:jobs.writing_email")
        : t("studio:jobs.writing_document");
  };

  return (
    <div
      role="status"
      className="fixed bottom-4 end-4 z-40 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2"
    >
      {jobs.map((job) => (
        <div
          key={job.key}
          className="flex items-center gap-3 rounded-xl border border-primary/30 bg-surface px-3 py-2 text-sm shadow-lg"
        >
          {job.phase === "generating" ? (
            <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
          ) : job.phase === "error" ? (
            <AlertCircle className="size-4 shrink-0 text-danger" />
          ) : (
            <Sparkles className="size-4 shrink-0 text-primary" />
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium text-foreground">{title(job)}</p>
            <p className="truncate text-xs text-muted-foreground">
              {job.phase === "error" ? job.message : job.prompt || job.liveLabel}
            </p>
          </div>
          {job.phase === "error" ? (
            <Button
              type="button"
              variant="outline"
              size="xs"
              className="shrink-0"
              onClick={() => void rebuildStudioJob(job.key)}
            >
              <RotateCcw className="size-3" />
              {t("studio:jobs.rebuild")}
            </Button>
          ) : (
            <Link
              to={pathFor(job)}
              className="shrink-0 font-medium text-primary hover:underline"
            >
              {t("studio:jobs.open")}
            </Link>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={
              job.phase === "generating"
                ? t("studio:jobs.cancel")
                : t("studio:jobs.dismiss")
            }
            title={
              job.phase === "generating"
                ? t("studio:jobs.cancel")
                : t("studio:jobs.dismiss")
            }
            onClick={() => takeStudioJob(job.key)}
          >
            <X className="size-3.5" />
          </Button>
        </div>
      ))}
    </div>
  );
};
